import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Tabs, Tab, Alert, Button } from "@mui/material";
import PublicIcon from "@mui/icons-material/Public";
import NewListComponent from "../../Templates/NewListComponent";
import UploadLogModal from "./UploadLogModal";
import NewComparisonModal from "./NewComparisonModal";
import LogThresholds from "./LogThresholds";
import { ACCENT } from "./accent";
import { useAuthGate } from "../../../hooks/useAuthGate";
import {
  listLogs,
  deleteLog,
  setLogPublic,
  pollLogUntilDone,
  listComparisons,
  createComparison,
  deleteComparison,
  getBulkDownloadUrls,
} from "./api/newTelemetry";
import { startDownloads } from "./downloadLogs";
import { logPath, comparePath, PUBLIC_BASE } from "./paths";
import { Link as RouterLink } from "react-router-dom";

const isInProgress = (status) => status === "pending" || status === "parsing";

const STATUS_OPTIONS = [
  { value: "ready", label: "Ready" },
  { value: "parsing", label: "Parsing" },
  { value: "pending", label: "Pending" },
  { value: "failed", label: "Failed" },
];

// Zero-padded so the column sorts correctly as plain text.
const fmtDuration = (s) => {
  if (s == null) return "";
  const t = Math.round(s);
  const p = (n) => String(n).padStart(2, "0");
  return `${p(Math.floor(t / 3600))}:${p(Math.floor((t % 3600) / 60))}:${p(t % 60)}`;
};

// `YYYY-MM-DD HH:mm` — human-readable and lexically chronological, so the
// house table's text sort on this column is also a real date sort.
const fmtCreated = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const LogAnalyzer = () => {
  const navigate = useNavigate();
  const { isAuthenticated, requireSignin } = useAuthGate();
  const [tab, setTab] = useState(0);

  const [logs, setLogs] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);

  const [comparisons, setComparisons] = useState([]);
  const [cmpLoading, setCmpLoading] = useState(true);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);

  // Built-in search of NewListComponent hands text back through handleNext;
  // we keep it here and derive the visible rows client-side.
  const [logSearch, setLogSearch] = useState("");
  const [cmpSearch, setCmpSearch] = useState("");

  // selectedRows for each table hold the mapped row objects (they keep `id`).
  const [selectedLogRows, setSelectedLogRows] = useState([]);
  const [selectedCmpRows, setSelectedCmpRows] = useState([]);

  const pollingRef = useRef(new Set());

  const refreshLogs = useCallback(async () => {
    try {
      const data = await listLogs({ pageSize: 100 });
      setLogs(data.logs || []);
      setListError(null);
      return data.logs || [];
    } catch (e) {
      setListError(e.message);
      return [];
    } finally {
      setListLoading(false);
    }
  }, []);

  const refreshComparisons = useCallback(async () => {
    try {
      const data = await listComparisons({ pageSize: 100 });
      setComparisons(data.comparisons || []);
    } catch {
      /* comparisons are optional — leave empty on error */
    } finally {
      setCmpLoading(false);
    }
  }, []);

  const watch = useCallback(
    async (logId) => {
      if (pollingRef.current.has(logId)) return;
      pollingRef.current.add(logId);
      try {
        await pollLogUntilDone(logId);
      } finally {
        pollingRef.current.delete(logId);
        refreshLogs();
      }
    },
    [refreshLogs]
  );

  useEffect(() => {
    // Flight logs + comparisons are per-user (auth-gated); show the tool publicly
    // with a sign-in nudge instead of 401s.
    if (!isAuthenticated) {
      setListLoading(false);
      setCmpLoading(false);
      return;
    }
    (async () => {
      const initial = await refreshLogs();
      initial.filter((l) => isInProgress(l.status)).forEach((l) => watch(l.id));
    })();
    refreshComparisons();
  }, [isAuthenticated, refreshLogs, refreshComparisons, watch]);

  const handleUploaded = async (logId) => {
    await refreshLogs();
    watch(logId);
    navigate(logPath(logId));
  };

  const handleDeleteLogs = async () => {
    const rows = [...selectedLogRows];
    setSelectedLogRows([]);
    for (const r of rows) {
      try {
        await deleteLog(r.id);
      } catch (e) {
        setListError(e.message);
      }
    }
    refreshLogs();
  };

  const downloadSelected = async () => {
    const rows = [...selectedLogRows];
    setSelectedLogRows([]);
    try {
      const { downloads, requested } = await getBulkDownloadUrls(rows.map((r) => r.id));
      if (downloads.length === 0) {
        setListError("None of the selected logs could be downloaded.");
        return;
      }
      // A log can be absent from the response if it was deleted between listing
      // and asking, so say so rather than silently downloading fewer files.
      if (downloads.length < requested) {
        setListError(`${downloads.length} of ${requested} logs are available to download.`);
      }
      startDownloads(downloads);
    } catch (e) {
      setListError(e.message);
    }
  };

  const setSelectedVisibility = async (isPublic) => {
    const rows = selectedLogRows.filter((r) => r.status === "ready");
    setSelectedLogRows([]);
    for (const r of rows) {
      try {
        await setLogPublic(r.id, isPublic);
      } catch (e) {
        setListError(e.message);
      }
    }
    refreshLogs();
  };

  // Per-row switch in the Public column. Optimistic, reverts on failure.
  const toggleRowPublic = useCallback(async (row, isPublic) => {
    const patch = (id, value) =>
      setLogs((cur) => cur.map((l) => (l.id === id ? { ...l, is_public: value } : l)));
    patch(row.id, isPublic);
    try {
      await setLogPublic(row.id, isPublic);
    } catch (e) {
      patch(row.id, !isPublic);
      setListError(e.message);
    }
  }, []);

  const handleDeleteComparisons = async () => {
    const rows = [...selectedCmpRows];
    setSelectedCmpRows([]);
    for (const r of rows) {
      await deleteComparison(r.id).catch(() => {});
    }
    refreshComparisons();
  };

  // First selected (ready) log is the base, the rest are compared against it.
  const handleCompareSelected = async () => {
    const ready = selectedLogRows.filter((r) => r.status === "ready");
    if (ready.length < 2) {
      setListError("Select at least two parsed logs to compare.");
      return;
    }
    const [base, ...others] = ready;
    try {
      const cmp = await createComparison({
        name: `${base.file_name} vs ${others.length} log${others.length > 1 ? "s" : ""}`,
        baseLogId: base.id,
        compareLogIds: others.map((r) => r.id),
      });
      setSelectedLogRows([]);
      await refreshComparisons();
      navigate(comparePath(cmp.id));
    } catch (e) {
      setListError(e.message);
    }
  };

  const goLog = (row) => navigate(logPath(row.id));
  const goCmp = (row) => navigate(comparePath(row.id));

  const logColumns = useMemo(
    () => [
      { id: "file_name", label: "Name", type: "textfield", display: true, onClick: goLog },
      { id: "tags", label: "Tags", type: "Array", display: true, onClick: goLog },
      { id: "vehicle_type", label: "Vehicle", type: "textfield", display: true, onClick: goLog },
      { id: "duration_display", label: "Duration", type: "textfield", display: true, onClick: goLog },
      { id: "created_display", label: "Created", type: "textfield", display: true, onClick: goLog },
      { id: "status", label: "Status", type: "dropdown", display: true, options: STATUS_OPTIONS, onClick: goLog },
      {
        id: "is_public",
        label: "Public",
        type: "switch",
        display: true,
        onChange: toggleRowPublic,
        isDisabled: (row) => row.status !== "ready",
      },
    ],
    // goLog is stable (navigate identity is stable) — safe to memoise once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toggleRowPublic]
  );

  const cmpColumns = useMemo(
    () => [
      { id: "name", label: "Name", type: "textfield", display: true, onClick: goCmp },
      { id: "logs_display", label: "Logs", type: "textfield", display: true, onClick: goCmp },
      { id: "created_display", label: "Created", type: "textfield", display: true, onClick: goCmp },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const logRows = useMemo(() => {
    const q = logSearch.trim().toLowerCase();
    return logs
      .filter((l) => {
        if (!q) return true;
        return (
          l.file_name.toLowerCase().includes(q) ||
          (l.tags || []).some((t) => t.toLowerCase().includes(q))
        );
      })
      .map((l) => ({
        ...l,
        logId: l.id, // selection identity key (object="log")
        tags: l.tags || [],
        duration_display: fmtDuration(l.duration_s),
        created_display: fmtCreated(l.created_at),
      }));
  }, [logs, logSearch]);

  const cmpRows = useMemo(() => {
    const q = cmpSearch.trim().toLowerCase();
    return comparisons
      .filter((c) => !q || c.name.toLowerCase().includes(q))
      .map((c) => ({
        ...c,
        comparisonId: c.id, // selection identity key (object="comparison")
        logs_display: `1 + ${c.compare_log_ids?.length ?? 0}`,
        created_display: fmtCreated(c.created_at),
      }));
  }, [comparisons, cmpSearch]);

  const canCompare = selectedLogRows.filter((r) => r.status === "ready").length >= 2;

  const canSetVisibility = selectedLogRows.some((r) => r.status === "ready");

  const logActions = [
    { value: "compare", label: "Compare selected", onClick: handleCompareSelected, disabled: !canCompare },
    {
      value: "make_public",
      label: "Make public",
      onClick: () => setSelectedVisibility(true),
      disabled: !canSetVisibility,
    },
    {
      value: "make_private",
      label: "Make private",
      onClick: () => setSelectedVisibility(false),
      disabled: !canSetVisibility,
    },
    {
      value: "download",
      label: selectedLogRows.length > 1 ? `Download ${selectedLogRows.length} logs` : "Download",
      onClick: downloadSelected,
      disabled: selectedLogRows.length === 0,
    },
    { value: "delete", label: "Delete", disabled: selectedLogRows.length === 0 },
  ];

  const cmpActions = [{ value: "delete", label: "Delete", disabled: selectedCmpRows.length === 0 }];

  return (
    <Box sx={{ height: "100%", overflow: "auto" }}>
      {!isAuthenticated && (
        <Alert
          severity="info"
          sx={{ mx: 4, mt: 2 }}
          action={
            <Button size="small" onClick={() => requireSignin()} sx={{ color: ACCENT, textTransform: "none", fontWeight: 600 }}>
              Sign in
            </Button>
          }
        >
          You’re viewing the Log Analyzer publicly. Sign in to upload flight logs, run comparisons and analyze with the assistant.
        </Alert>
      )}
      <Box sx={{ px: 4, pt: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              "& .MuiTabs-indicator": { backgroundColor: ACCENT },
              "& .Mui-selected": { color: `${ACCENT} !important` },
            }}
          >
            <Tab label={`Logs${logs.length ? ` (${logs.length})` : ""}`} sx={{ textTransform: "none" }} />
            <Tab
              label={`Comparisons${comparisons.length ? ` (${comparisons.length})` : ""}`}
              sx={{ textTransform: "none" }}
            />
            <Tab label="Thresholds" sx={{ textTransform: "none" }} />
          </Tabs>
          <Button
            size="small"
            startIcon={<PublicIcon sx={{ fontSize: 16 }} />}
            component={RouterLink}
            to={PUBLIC_BASE}
            sx={{ color: ACCENT, textTransform: "none", fontWeight: 600 }}
          >
            View public gallery
          </Button>
        </Box>
        {listError && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {listError}
          </Typography>
        )}
      </Box>

      {tab === 0 && (
        <NewListComponent
          title="Flight Logs"
          subtitle="Analyse ArduPilot flight logs · Select two or more to compare"
          columns={logColumns}
          data={logRows}
          object="log"
          isLoading={listLoading}
          showAddButton
          addButtonText="Upload log"
          onAddClick={() => requireSignin(() => setUploadOpen(true))}
          setShowAddForm={() => requireSignin(() => setUploadOpen(true))}
          onRefresh={refreshLogs}
          showActions
          actionOptions={logActions}
          onDeleteClick={handleDeleteLogs}
          selectedRows={selectedLogRows}
          setSelectedRows={setSelectedLogRows}
          setFormObject={() => {}}
          sortBy="created_display"
          sortingOrder="desc"
          limit={10}
          setData={() => {}}
          handleNext={({ searchText } = {}) => setLogSearch(searchText ?? "")}
        />
      )}

      {tab === 1 && (
        <NewListComponent
          title="Comparisons"
          subtitle="Saved side-by-side comparisons of flight logs"
          columns={cmpColumns}
          data={cmpRows}
          object="comparison"
          isLoading={cmpLoading}
          showAddButton
          addButtonText="New comparison"
          onAddClick={() => requireSignin(() => setCompareOpen(true))}
          setShowAddForm={() => requireSignin(() => setCompareOpen(true))}
          onRefresh={refreshComparisons}
          showActions
          actionOptions={cmpActions}
          onDeleteClick={handleDeleteComparisons}
          selectedRows={selectedCmpRows}
          setSelectedRows={setSelectedCmpRows}
          setFormObject={() => {}}
          sortBy="created_display"
          sortingOrder="desc"
          limit={10}
          setData={() => {}}
          handleNext={({ searchText } = {}) => setCmpSearch(searchText ?? "")}
        />
      )}

      {tab === 2 && (
        <Box sx={{ px: 4, py: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            Finding thresholds
          </Typography>
          <LogThresholds />
        </Box>
      )}

      <UploadLogModal open={uploadOpen} onClose={() => setUploadOpen(false)} onUploaded={handleUploaded} />
      <NewComparisonModal
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        logs={logs.filter((l) => l.status === "ready")}
        onCreated={(cid) => {
          refreshComparisons();
          navigate(comparePath(cid));
        }}
      />
    </Box>
  );
};

export default LogAnalyzer;
